import {type FC, type ChangeEvent, type FormEvent, useCallback, useEffect, useRef, useState} from 'react';
import {
    ActionButton, BroadcastGrid, BroadcastWrapper,
    ButtonGroup, ControlPanel, ControlSection, ControlTitle,
    DevicePreviewGrid, DevicePreviewItem, PageTitle, PermissionButton,
    PermissionRow, PreparationBox, SelectDevice, VideoPanel,
} from './types/Broadcast.styled.ts';
import Webcam from 'react-webcam';
import ConfSender from './components/ConfSender.tsx';
import type {Device, Options} from './types';
import callApi from '../../apis/callApi.ts';
import IconCamera from '../../assets/icons/icon-camera.png';
import IconMicrophone from '../../assets/icons/icon-mic-off.png';
import IconCameraOff from '../../assets/icons/icon-camera-off.png';
import IconMicrophoneOff from '../../assets/icons/icon-mic-off.png';
import Package from '../../../package.json';

const BroadcastPage: FC = () => {
    const [peerConnection, setPeerConnection] = useState<RTCPeerConnection | null>(null);
    const [dataChannel, setDataChannel] = useState<RTCDataChannel | null>(null);
    const localConnection = useRef<RTCPeerConnection | null>(null);
    const remoteConnection = useRef<RTCPeerConnection | null>(null);

    useEffect(() => {
        const servers: RTCConfiguration = {};
        const localPeerConnection = new RTCPeerConnection(servers);
        const remotePeerConnection = new RTCPeerConnection(servers);

        const dataChannel = localPeerConnection.createDataChannel('broadcastData');
        setDataChannel(dataChannel);

        localPeerConnection.onicecandidate = (event) => {
            if (event.candidate) {
                remotePeerConnection.addIceCandidate(event.candidate);
            }
        };

        remotePeerConnection.onicecandidate = (event) => {
            if (event.candidate) {
                localPeerConnection.addIceCandidate(event.candidate);
            }
        };

        remotePeerConnection.ondatachannel = (event) => {
            const receiveChannel = event.channel;
            receiveChannel.onmessage = (event) => {
                const newData = JSON.parse(event.data);
                // Note: localStorage is not available in Claude artifacts
                // In a real environment, you would use: localStorage.setItem("broadcastData", JSON.stringify(newData));

                if (newData.action === 'toggleMute') {
                    // Mute/unmute remote audio tracks
                    const remoteAudioTracks = remoteConnection.current
                        ?.getReceivers()
                        .map((receiver) => receiver.track)
                        .filter((track) => track?.kind === 'audio');
                    remoteAudioTracks?.forEach((track) => {
                        if (track) track.enabled = !newData.mute;
                    });
                }
            };
        };

        localPeerConnection
            .createOffer()
            .then((offer) => localPeerConnection.setLocalDescription(offer))
            .then(() =>
                // @ts-ignore
                remotePeerConnection.setRemoteDescription(localPeerConnection.localDescription)
            )
            .then(() => remotePeerConnection.createAnswer())
            .then((answer) => remotePeerConnection.setLocalDescription(answer))
            .then(() =>
                // @ts-ignore
                localPeerConnection.setRemoteDescription(remotePeerConnection.localDescription)
            );
        localConnection.current = localPeerConnection;
        remoteConnection.current = remotePeerConnection;
        setPeerConnection(localPeerConnection);
        return () => {
            localPeerConnection.close();
            remotePeerConnection.close();
        };
    }, []);

    const [isDisabledStart, setIsDisabledStart] = useState<boolean>(false);
    const [isDisabledStop, setIsDisabledStop] = useState<boolean>(false);
    const [isDisabledStreaming, setIsDisabledStreaming] = useState<boolean>(false);
    const [isDisabledIPTV, setIsDisabledIPTV] = useState<boolean>(false);

    const [isStreamingProcessing, setIsStreamingProcessing] = useState<boolean>(false);
    const [isIPTVProcessing, setIsIPTVProcessing] = useState<boolean>(false);

    const updateStatus = async (currentStatus: string): Promise<void> => {
        await callApi().put('/tvs/broadcast-status/', {status: currentStatus});
        if (currentStatus === 'iptv') {
            setTimeout(() => {
                updateStatus('free');
                setTimeout(() => {
                    setIsDisabledStart(false);
                    setIsDisabledStop(false);
                    setIsDisabledStreaming(false);
                    setIsDisabledIPTV(false);
                    setIsIPTVProcessing(false);
                }, 3000);
            }, 11000);
        } else if (currentStatus === 'streaming') {
            setTimeout(() => {
                updateStatus('free');
                setTimeout(() => {
                    setIsDisabledIPTV(false);
                    setIsDisabledStart(false);
                    setIsDisabledStop(false);
                    setIsDisabledStreaming(false);
                    setIsStreamingProcessing(false);
                }, 3000);
            }, 11000);
        }
    };

    // 스트리밍 전환 버튼
    const handleStreamingClick = async (): Promise<void> => {
        if (isDisabledStreaming) return;
        setIsDisabledStreaming(true);
        setIsDisabledIPTV(true);
        setIsDisabledStart(true);
        setIsDisabledStop(true);
        setIsStreamingProcessing(true);
        await updateStatus('streaming');
    };

    // IPTV 전환 버튼
    const handleIPTVClick = async (): Promise<void> => {
        if (isDisabledIPTV) return;
        setIsDisabledStart(true);
        setIsDisabledStop(true);
        setIsDisabledStreaming(true);
        setIsDisabledIPTV(true);
        setIsIPTVProcessing(true);
        await updateStatus('iptv');
    };

    const [token, setToken] = useState<string>('');
    const getJWT = async () => {
        const {data} = await callApi().post(`jitsi/token/`, {user: '스튜디오'});
        setToken(data.token.slice(2, -1));
    };
    useEffect(() => {
        getJWT();
    }, []);

    const [options, setOptions] = useState<Options>({
        device: '1',
        camera: true,
        microphone: true,
    });
    const [renderBroadCast, setRenderBroadCast] = useState<boolean>(false);
    const handleDevice = (e: ChangeEvent<HTMLSelectElement>): void => {
        setOptions({...options, device: e.target.value});
    };
    const [devices, setDevices] = useState<Device[]>([]);
    const handleDevices = useCallback((mediaDevices: MediaDeviceInfo[]): void => {
        const videoDevices = mediaDevices.filter(({kind}) => kind === 'videoinput');
        setDevices(videoDevices as Device[]);
        setOptions({
            ...options,
            device: videoDevices[0]?.deviceId || '',
        });
    }, []);

    useEffect(() => {
        if (navigator && navigator.mediaDevices && navigator.mediaDevices.enumerateDevices) {
            navigator.mediaDevices.enumerateDevices().then(handleDevices);
        } else {
            console.error('enumerateDevices is not supported');
        }
    }, [handleDevices]);

    const handleStart = (e: FormEvent): void => {
        e.preventDefault();
        if (isDisabledStart) return;
        setRenderBroadCast(true);
        handleStreamingClick();
        setIsDisabledStart(true);
        setIsDisabledStreaming(true);
        setIsDisabledIPTV(true);
        setIsDisabledStop(true);
        setIsStreamingProcessing(false);
        setTimeout(() => {
            setIsDisabledStart(false);
            setIsDisabledStop(false);
            setIsDisabledStreaming(false);
            setIsDisabledIPTV(false);
        }, 15000);
    };

    const handleStop = async (e: FormEvent): Promise<void> => {
        e.preventDefault();
        setRenderBroadCast(false);
        await updateStatus('free');
    };

    const handleToggleMute = (): void => {
        // 로컬 오디오 트랙 mute/unmute
        const localAudioTracks = localConnection.current
            ?.getSenders()
            .map((sender) => sender.track)
            .filter((track) => track?.kind === 'audio');
        localAudioTracks?.forEach((track) => {
            if (track) track.enabled = !track.enabled;
        });

        // 모든 원격 참가자들에게 mute/unmute 명령 전송
        const newData = {action: 'toggleMute', mute: !options.microphone};
        if (dataChannel && dataChannel.readyState === 'open') {
            dataChannel.send(JSON.stringify(newData));
        }

        setOptions({...options, microphone: !options.microphone});
    };

    return (
        <BroadcastWrapper>
            <PageTitle>방송 송출 관리</PageTitle>
            <BroadcastGrid>
                {/* ------------------------- LEFT: VIDEO SECTION ------------------------- */}
                <VideoPanel>
                    {renderBroadCast ? (
                        <ConfSender token={token}/>
                    ) : (
                        <>
                            <PreparationBox>
                                <h3>방송 송출 준비중입니다.</h3>
                                <h6>(ver. {Package.version})</h6>
                                <DevicePreviewGrid>
                                    {devices.map((device, index) => (
                                        <DevicePreviewItem key={index}>
                                            <Webcam
                                                audio={false}
                                                videoConstraints={{
                                                    deviceId: device.deviceId,
                                                }}
                                                style={{height: '300px', borderRadius: '8px'}}
                                            />
                                            <h4>{device.label || `Device ${index + 1}`}</h4>
                                        </DevicePreviewItem>
                                    ))}
                                </DevicePreviewGrid>
                            </PreparationBox>
                        </>
                    )}
                </VideoPanel>

                {/* ------------------------- RIGHT: CONTROL PANEL ------------------------- */}
                <ControlPanel>
                    {/* Device Select */}
                    <ControlSection>
                        <ControlTitle>장치 선택</ControlTitle>
                        <SelectDevice value={options.device} onChange={handleDevice}>
                            {devices.map((device, index) => (
                                <option key={index} value={device.deviceId}>
                                    {device.label || `Device ${index + 1}`}
                                </option>
                            ))}
                        </SelectDevice>
                    </ControlSection>

                    {/* Camera / Microphone Permission */}
                    <ControlSection>
                        <ControlTitle>권한 선택</ControlTitle>
                        <PermissionRow>
                            <PermissionButton $disabled={!options.camera}>
                                <img src={options.camera ? IconCamera : IconCameraOff} alt="camera"/>
                            </PermissionButton>

                            <PermissionButton $disabled={!options.microphone} onClick={handleToggleMute}>
                                <img src={options.microphone ? IconMicrophone : IconMicrophoneOff} alt="microphone"/>
                            </PermissionButton>
                        </PermissionRow>
                    </ControlSection>

                    {/* Start / Stop / Streaming / IPTV */}
                    <ControlSection>
                        <ControlTitle>방송 송출</ControlTitle>

                        <ButtonGroup>
                            <ActionButton $variant="primary" $disabled={isDisabledStart} onClick={handleStart}>
                                시작
                            </ActionButton>

                            <ActionButton $variant="secondary" $disabled={isDisabledStop} onClick={handleStop}>
                                중지
                            </ActionButton>

                            <ActionButton
                                $variant="danger"
                                $disabled={isDisabledStreaming}
                                onClick={handleStreamingClick}
                            >
                                {isStreamingProcessing ? '스트리밍 전환 중' : '스트리밍 전환'}
                            </ActionButton>

                            <ActionButton $variant="danger" $disabled={isDisabledIPTV} onClick={handleIPTVClick}>
                                {isIPTVProcessing ? 'IPTV 전환 중' : 'IPTV 전환'}
                            </ActionButton>
                        </ButtonGroup>
                    </ControlSection>
                </ControlPanel>
            </BroadcastGrid>
        </BroadcastWrapper>
    );
};

export default BroadcastPage;
